import CONSTANTS from 'src/utils/constants';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DatasetRecord } from 'src/models/tp-allocation/DatasetRecord';
import WorksheetTotal from 'src/models/tp-allocation/WorksheetTotal';
import { LockInfo } from 'src/models/common/LockInfo';
import { deriveViewModeFromLockInfo } from 'src/components/shared/edit-locking/editLockUtils';

/** State of the derived allocation sheet builder page. */
export interface DerivedAllocationSheetBuilderState {
    sheet: DerivedAllocationSheetData | null;
    availableVersions: number[];
    /** Who holds the edit lock on the sheet, from the last read; null when nobody does. */
    lockInfo: LockInfo | null;
    loading: boolean;
    error?: string;
    viewMode: string;
    /** Saved Step 1 rows, read by the later steps for formula suggestions. */
    dataSources: DatasetRecord[];
    /** Step 2 rows, read by Step 3. */
    totals: WorksheetTotal[];
    /** Incremented to refetch the sheet header after a write. */
    sheetRefreshToken: number;
    /** Incremented to refetch Step 2 after a Step 1 row is renamed. */
    totalsRefreshToken: number;
    /** Steps with unsaved rows or a save in flight; another version cannot be opened until they are saved. */
    unsavedSteps: number[];
    /** True while an Execute run of the draft is in progress. */
    executing: boolean;
    /** Incremented after an Execute run, to reload the steps with the run's values. */
    stepsRevision: number;
    /** Steps with a save in flight. */
    savingSteps: number[];
}

export const initialState: DerivedAllocationSheetBuilderState = {
    sheet: null,
    availableVersions: [],
    lockInfo: null,
    loading: true,
    viewMode: CONSTANTS.VIEW_MODE.READ_ONLY,
    dataSources: [],
    totals: [],
    sheetRefreshToken: 0,
    totalsRefreshToken: 0,
    unsavedSteps: [],
    executing: false,
    stepsRevision: 0,
    savingSteps: [],
};

export interface DeriveDasViewModeArgs {
    canEdit: boolean;
    status?: WorksheetStatus;
    sheetVersion?: number;
    executing?: boolean;
    /** Whether edit locking is on for the current user; null while the flag is still loading. */
    editLockingEnabled?: boolean | null;
    lockInfo?: LockInfo | null;
    currentUser?: string;
}

/**
 * View mode of the builder. Only the draft version in DRAFT_IN_PROGRESS can be edited:
 * 1. no edit permission: FROZEN
 * 2. any other status or version, or a run in progress (an edit during it fails the run): READ_ONLY
 * 3. edit locking flag still loading: READ_ONLY
 * 4. edit locking on: EDITABLE only while the current user holds the lock, else READ_ONLY
 * 5. otherwise EDITABLE
 */
export function deriveDasViewMode(args: DeriveDasViewModeArgs): string {
    const { canEdit, status, sheetVersion, executing, editLockingEnabled = false, lockInfo, currentUser } = args;
    if (!canEdit) {
        return CONSTANTS.VIEW_MODE.FROZEN;
    }
    if (status !== 'DRAFT_IN_PROGRESS' || sheetVersion !== CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION || executing) {
        return CONSTANTS.VIEW_MODE.READ_ONLY;
    }
    if (editLockingEnabled == null) {
        return CONSTANTS.VIEW_MODE.READ_ONLY;
    }
    if (editLockingEnabled) {
        return deriveViewModeFromLockInfo(lockInfo, currentUser);
    }
    return CONSTANTS.VIEW_MODE.EDITABLE;
}
